import React, { useEffect, useMemo, useState } from 'react';
import { Page, NotifyModal } from 'components';
import Header from 'components/Header/Header';
import { Col, Row } from 'react-bootstrap';
import { getAllBranches, getEquipmentDetails, getEquipmentsByRegion, sendUpdateSelection, getUpdates } from 'api/updatesService';
import { getUnits } from 'api/companyService';
import VersionDistributionChart from './components/VersionDistributionChart';
import EquipmentSelector from './components/EquipmentSelector';
import UpdatePreviewModal from './components/UpdatePreviewModal';
import Reload from 'components/Reload/Reload';

const isSimtEligible = (eq) => {
  const isSIMT = typeof eq?.id === 'string' && eq.id.startsWith('SIMT-');
  const isSwMt = typeof eq?.currentVersion === 'string' && eq.currentVersion.startsWith('SW.MT.00');
  return isSIMT && isSwMt;
};

// Deriva a última versão válida a partir da resposta de getAllBranches
// Prioriza versões que começam com 'SW.MT.'; mantém 'master' e 'TEST_VERSION' apenas como fallback
const deriveLatestVersion = (branchesData) => {
  if (!branchesData) return '';
  const list = Array.isArray(branchesData?.versions)
    ? branchesData.versions
    : Array.isArray(branchesData)
      ? branchesData
      : [];
  const versions = list.filter(v => typeof v === 'string');
  if (!versions.length) return '';
  // Mantém ordem recebida (assumindo que a API já entrega em ordem cronológica decrescente ou crescente)
  // Filtra primeiro somente SW.MT.*; se vazio, cai para qualquer versão
  const swmt = versions.filter(v => /^SW\.MT\./.test(v));
  const pool = swmt.length ? swmt : versions;
  // Último elemento (assumindo que o backend retorna do mais antigo para o mais novo). Caso contrário inverter aqui.
  const last = pool[pool.length - 1];
  return last || '';
};

const Updates = () => {
  const [loading, setLoading] = useState(true);
  const [equipments, setEquipments] = useState([]);
  const [regionGroups, setRegionGroups] = useState({ states: {}, cities: {} });
  const [unitGroups, setUnitGroups] = useState({}); // { unitId: { name, equipments: [], raw } }
  const [latestVersion, setLatestVersion] = useState('');
  const [latestVersions, setLatestVersions] = useState([]); // até 2 últimas versões

  // Retorna até as duas últimas versões seguindo a mesma lógica de filtragem
  const deriveLastTwoVersions = (branchesData) => {
    if (!branchesData) return [];
    const list = Array.isArray(branchesData?.versions)
      ? branchesData.versions
      : Array.isArray(branchesData)
        ? branchesData
        : [];
    const versions = list.filter(v => typeof v === 'string');
    if (!versions.length) return [];
    const swmt = versions.filter(v => /^SW\.MT\./.test(v));
    const pool = swmt.length ? swmt : versions;
    // pega as duas últimas mantendo ordem do mais antigo para o mais novo e depois reorganiza para mostrar mais recente primeiro
    const lastTwo = pool.slice(-2);
    return lastTwo.reverse(); // índice 0 = mais recente
  };

  // Seleção e UI
  const [mode, setMode] = useState('equipment'); // 'equipment' | 'state' | 'city' | 'unit'
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState(new Set());
  const [selectedEquipments, setSelectedEquipments] = useState(new Set());
  const [showPreview, setShowPreview] = useState(false);
  const [previewPayload, setPreviewPayload] = useState(null);
  const [savedLastVersion, setSavedLastVersion] = useState('');

  const [type, setType] = useState({ color: "", status: "" });
  const [modalShow, setModalShow] = useState(false);

  const handleAlertModalSuccess = (msg) => {
    setType({ "color": "#198754", "status": msg })
    setModalShow(true)
  }

  const handleAlertModalError = (msg) => {
    setType({ "color": "red", "status": msg })
    setModalShow(true)
  }

  // Buscar dados
  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [branches, details, groups, unitsRaw] = await Promise.all([
          getAllBranches().catch(() => []),
          getEquipmentDetails(),
          getEquipmentsByRegion(),
          getUnits().catch(() => [])
        ]);

        setEquipments(Array.isArray(details) ? details : []);
        setRegionGroups(groups || { states: {}, cities: {} });
        // Derivar última versão diretamente do retorno de getAllBranches
        const last = deriveLatestVersion(branches);
        setLatestVersion(last);

        const two = deriveLastTwoVersions(branches);
        setLatestVersions(two);

        // Mapear unidades -> equipamentos elegíveis existentes
        const unitsArray = Array.isArray(unitsRaw) ? unitsRaw : unitsRaw ? [unitsRaw] : [];
        const uMap = {};
        unitsArray.forEach((unit) => {
          if (!unit) return;
          const serialsA = Array.isArray(unit.equipments) ? unit.equipments : [];
          const serialsB = Array.isArray(unit.localEquipament) ? unit.localEquipament.map(le => le.serial).filter(Boolean) : [];
          const allSerials = Array.from(new Set([...serialsA, ...serialsB]));
          const matched = allSerials
            .map(sn => (Array.isArray(details) ? details.find(eq => eq.id === sn) : null))
            .filter(Boolean)
            .filter(isSimtEligible);
          if (matched.length) {
            const key = unit._id || unit.name || Math.random().toString(36).slice(2);
            uMap[key] = {
              name: unit.name || key,
              equipments: matched,
              raw: unit
            };
          }
        });
        setUnitGroups(uMap);
      } catch (e) {
        setEquipments([]);
        setRegionGroups({ states: {}, cities: {} });
        console.error('Erro ao carregar dados:', e?.message || e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // Carregar último estado salvo (configurações de seleção previamente enviadas)
  useEffect(() => {
    // Só tenta quando já temos dados de equipamentos e grupos (evita corrida)
    if (!equipments.length) return;
    const loadSavedConfig = async () => {
      try {
        const saved = await getUpdates();
        if (!saved) return;
        // Pode vir lista ou objeto único
        const record = Array.isArray(saved) ? saved[saved.length - 1] : saved;
        if (!record) return;

        const recVersion = record.lastVersion || '';
        setSavedLastVersion(recVersion);

        // Se a versão salva for diferente da versão mais recente, limpa seleção e ignora config antiga
        if (recVersion && latestVersion && recVersion !== latestVersion) {
          setSelectedEquipments(new Set());
          setPreviewPayload(null);
          setShowPreview(false);
          return;
        }

        // Equipamentos explicitamente salvos
        const idsSet = new Set();
        if (Array.isArray(record.equipments) && record.equipments.length) {
          record.equipments.forEach((id) => idsSet.add(id));
        } else {
          // Derivar de estados/cidades caso não haja lista direta
          if (Array.isArray(record.statesUpdate)) {
            record.statesUpdate.forEach((stateKey) => {
              const stateGroup = regionGroups.states[stateKey];
              stateGroup?.equipments?.forEach((eq) => idsSet.add(eq.id));
            });
          }
          if (Array.isArray(record.citysUpdate)) {
            record.citysUpdate.forEach((cityKey) => {
              const cityGroup = regionGroups.cities[cityKey];
              cityGroup?.equipments?.forEach((eq) => idsSet.add(eq.id));
            });
          }
        }

        if (idsSet.size) {
          // Mantém somente IDs válidos existentes atualmente
          const valid = new Set();
          idsSet.forEach((id) => {
            if (equipments.some((eq) => eq.id === id)) valid.add(id);
          });
          setSelectedEquipments(valid);
        }

      } catch (err) {
        console.error('Falha ao carregar configurações anteriores:', err?.message || err);
      }
    };
    loadSavedConfig();
  }, [equipments, regionGroups, latestVersion, unitGroups]);

  // Se a versão mais recente mudar (nova branch), zera a seleção/preview para forçar nova escolha
  useEffect(() => {
    if (!savedLastVersion || !latestVersion) return;
    if (savedLastVersion !== latestVersion) {
      setSelectedEquipments(new Set());
      setPreviewPayload(null);
      setShowPreview(false);
    }
  }, [savedLastVersion, latestVersion]);

  // Equipamentos elegíveis
  const eligibleEquipments = useMemo(
    () => equipments.filter(isSimtEligible),
    [equipments]
  );

  // Filtro aplicado
  const normalized = (s) => (s || '').toString().toLowerCase();
  const filterFn = (eq) => {
    if (!filter) return true;
    const q = normalized(filter);
    return [eq.id, eq.name, eq.location, eq.city, eq.state, eq.currentVersion]
      .some((v) => normalized(v).includes(q));
  };

  const filteredEligible = useMemo(
    () => eligibleEquipments.filter(filterFn),
    [eligibleEquipments, filter]
  );

  // Helpers de região
  const getRegionKeys = () => {
    if (mode === 'state') return Object.keys(regionGroups.states || {});
    if (mode === 'city') return Object.keys(regionGroups.cities || {});
    if (mode === 'unit') return Object.keys(unitGroups || {});
    return [];
  };

  const getRegionName = (key) => {
    if (mode === 'state') return regionGroups.states[key]?.name || key;
    if (mode === 'city') return regionGroups.cities[key]?.name || key;
    if (mode === 'unit') return unitGroups[key]?.name || key;
    return key;
  };

  const getRegionEquipments = (key) => {
    if (mode === 'state') return (regionGroups.states[key]?.equipments || []).filter(isSimtEligible).filter(filterFn);
    if (mode === 'city') return (regionGroups.cities[key]?.equipments || []).filter(isSimtEligible).filter(filterFn);
    if (mode === 'unit') return (unitGroups[key]?.equipments || []).filter(isSimtEligible).filter(filterFn);
    return [];
  };

  const getRegionSelectedCount = (key) => {
    const list = getRegionEquipments(key);
    if (!list.length) return 0;
    return list.reduce((acc, eq) => acc + (selectedEquipments.has(eq.id) ? 1 : 0), 0);
  };

  const isRegionFullySelected = (key) => {
    const list = getRegionEquipments(key);
    if (!list.length) return false;
    return list.every((eq) => selectedEquipments.has(eq.id));
  };

  // Handlers
  const toggleExpand = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const handleToggleEquipment = (id, checked) => {
    setSelectedEquipments((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };

  const handleToggleRegion = (key, checked) => {
    const list = getRegionEquipments(key);
    setSelectedEquipments((prev) => {
      const next = new Set(prev);
      list.forEach((eq) => { if (checked) next.add(eq.id); else next.delete(eq.id); });
      return next;
    });
  };

  const allFilteredSelected = useMemo(() => {
    if (mode !== 'equipment') return false;
    if (!filteredEligible.length) return false;
    return filteredEligible.every((eq) => selectedEquipments.has(eq.id));
  }, [mode, filteredEligible, selectedEquipments]);

  const toggleSelectAllFiltered = (checked) => {
    if (mode !== 'equipment') return;
    setSelectedEquipments((prev) => {
      const next = new Set(prev);
      filteredEligible.forEach((eq) => { if (checked) next.add(eq.id); else next.delete(eq.id); });
      return next;
    });
  };

  // Utilitários de seleção em massa por aba
  const getFilteredRegionKeys = () => {
    return getRegionKeys().filter((k) => normalized(getRegionName(k)).includes(normalized(filter)));
  };

  const selectAllRegionsVisible = () => {
    const keys = getFilteredRegionKeys();
    setSelectedEquipments((prev) => {
      const next = new Set(prev);
      keys.forEach((key) => {
        getRegionEquipments(key).forEach((eq) => next.add(eq.id));
      });
      return next;
    });
  };

  const clearRegionsVisible = () => {
    const keys = getFilteredRegionKeys();
    setSelectedEquipments((prev) => {
      const next = new Set(prev);
      keys.forEach((key) => {
        getRegionEquipments(key).forEach((eq) => next.delete(eq.id));
      });
      return next;
    });
  };

  const selectAllFilteredEquipments = () => {
    setSelectedEquipments((prev) => {
      const next = new Set(prev);
      filteredEligible.forEach((eq) => next.add(eq.id));
      return next;
    });
  };

  const clearFilteredEquipments = () => {
    setSelectedEquipments((prev) => {
      const next = new Set(prev);
      filteredEligible.forEach((eq) => next.delete(eq.id));
      return next;
    });
  };

  const visibleRegionsTotal = useMemo(() => {
    if (mode === 'equipment') return 0;
    const keys = getFilteredRegionKeys();
    return keys.reduce((acc, key) => acc + getRegionEquipments(key).length, 0);
  }, [mode, filter, regionGroups, equipments]);

  // Montagem do payload e envio
  const buildPayload = () => {
    // statesUpdate: nomes das UFs visíveis e com seleção total? Especificamos todos os estados que têm pelo menos um selecionado
    const statesUpdate = Object.keys(regionGroups.states || {}).filter((uf) => {
      const list = (regionGroups.states[uf]?.equipments || []).filter(isSimtEligible);
      return list.some((eq) => selectedEquipments.has(eq.id));
    });

    // citysUpdate: "UF - Cidade" onde há ao menos um selecionado
    const citysUpdate = Object.keys(regionGroups.cities || {}).filter((key) => {
      const list = (regionGroups.cities[key]?.equipments || []).filter(isSimtEligible);
      return list.some((eq) => selectedEquipments.has(eq.id));
    });

    const equipments = Array.from(selectedEquipments);
    const unitsUpdate = Object.keys(unitGroups || {}).filter((uk) => {
      const list = (unitGroups[uk]?.equipments || []).filter(isSimtEligible);
      return list.some((eq) => selectedEquipments.has(eq.id));
    });
    const allSelected = equipments.length > 0 && equipments.length === eligibleEquipments.length;
    return {
      lastVersion: latestVersion || '',
      allEquipmentsUpdate: allSelected,
      statesUpdate,
      citysUpdate,
      unitsUpdate,
      equipments
    };
  };

  const [submitting, setSubmitting] = useState(false);
  const handleSendUpdate = () => {
    const payload = buildPayload();
    console.log("Payload:", payload);

    if (!payload.lastVersion) {
      alert('Não foi possível determinar a última versão.');
      return;
    }
    setPreviewPayload(payload);
    setShowPreview(true);
  };

  const handleConfirmSend = async () => {
    try {
      setSubmitting(true);
      const payload = previewPayload || buildPayload();
      await sendUpdateSelection(payload);
      setShowPreview(false);
      handleAlertModalSuccess('Update realizado com sucesso!');
    } catch (e) {
      alert(`Falha ao enviar atualização: ${e?.message || e}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page className="p-3" title="Atualizações">

      <Header page={"Atualizações"} />

      <NotifyModal
        type={type}
        show={modalShow}
        onHide={() => setModalShow(false)}
      />

      {loading && <Reload />}

      <>
        <Row className="g-2">
          <Col xs={12} lg={6}>
            <VersionDistributionChart
              eligibleEquipments={eligibleEquipments}
              latestVersion={latestVersion}
              latestVersions={latestVersions}
            />
          </Col>
          <Col xs={12} lg={6}>
            <EquipmentSelector
              mode={mode}
              setMode={setMode}
              filter={filter}
              setFilter={setFilter}
              selectedEquipments={selectedEquipments}
              filteredEligible={filteredEligible}
              handleToggleEquipment={handleToggleEquipment}
              selectAllFilteredEquipments={selectAllFilteredEquipments}
              clearFilteredEquipments={clearFilteredEquipments}
              visibleRegionsTotal={visibleRegionsTotal}
              selectAllRegionsVisible={selectAllRegionsVisible}
              clearRegionsVisible={clearRegionsVisible}
              getRegionKeys={getRegionKeys}
              normalized={normalized}
              getRegionName={getRegionName}
              getRegionEquipments={getRegionEquipments}
              getRegionSelectedCount={getRegionSelectedCount}
              handleToggleRegion={handleToggleRegion}
              expanded={expanded}
              toggleExpand={toggleExpand}
              submitting={submitting}
              handleSendUpdate={handleSendUpdate}
            />
          </Col>
        </Row>
        <UpdatePreviewModal
          show={showPreview}
          onHide={() => setShowPreview(false)}
          previewPayload={previewPayload}
          submitting={submitting}
          onConfirm={handleConfirmSend}
        />
      </>

    </Page>
  );
};

export default Updates;

